import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * HIP API — ABDM Milestone 2 endpoints (Health Information Provider)
 *
 * This clinic publishing its OWN patients' visit records outward to ABDM —
 * the mirror of the M3 HIU flow (hiuApi.js), which pulls records IN.
 *
 * Design rules enforced here (same as hiuApi.js):
 *   1. Every function returns a normalized domain-shaped object.
 *      Components never see axios response envelopes or raw backend keys.
 *   2. Errors carry declarative metadata (retryable) so UI layers become
 *      conditional on flags, not string-matched messages.
 *   3. X-Correlation-Id is injected by the apiClient interceptor, not here.
 *   4. No URL construction outside this file.
 *   5. No ABHA identifier (number or address) is ever sent from here — every
 *      function below takes only patientId; the backend resolves ABHA
 *      identity server-side (matches the existing HIL functions in
 *      abhaApi.js, which follow the same rule).
 *
 * Base: /api/organisation/hip/
 *
 * Automatic visit-completion publishing defaults OFF (app/core/config.py:
 * abdm_publishing_enabled) — createCareContext() below is the PRIMARY
 * publish path, not a backfill tool.
 * ═══════════════════════════════════════════════════════════════════════
 */

const BASE = '/api/organisation/hip';

// ── Error handling ────────────────────────────────────────────────────

/** Leaner classification, mirroring hiuApi.js's _buildError — HIP errors are mostly
 * readable ABDMCareContextError messages, no multi-step OTP flow to distinguish. */
function _buildError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  // Contract error envelope: new errors are {detail:{code,message,field?}}, old ones a string.
  const structured = detail && typeof detail === 'object' ? detail : null;
  const message = typeof detail === 'string' ? detail : structured?.message || 'HIP request failed';

  let code = 'UNKNOWN';
  let retryable = false;
  if (structured?.code) {
    code = structured.code;
    retryable = status >= 500;
  } else if (!status) {
    code = 'NETWORK_ERROR';
    retryable = true;
  } else if (status === 404) {
    code = 'NOT_FOUND';
  } else if (status === 422 || status === 400) {
    code = 'VALIDATION_ERROR';
  } else if (status === 403) {
    code = 'FORBIDDEN';
  } else if (status >= 500) {
    code = 'SERVICE_UNAVAILABLE';
    retryable = true;
  }

  const err = new Error(message);
  err.code = code;
  err.status = status;
  err.retryable = retryable;
  err.userMessage = message;
  if (structured?.field) err.field = structured.field;
  const retryAfter = structured?.retry_after ?? error?.response?.data?.retry_after
    ?? error?.response?.headers?.['retry-after']; // seconds
  if (retryAfter) err.retryAfter = retryAfter;
  return err;
}
// Shared with hipRecordsApi.js so both HIP clients classify errors the same way.
export { _buildError as buildHipError };

// ── Registration / capabilities (read-only) ─────────────────────────────

/** @returns {{ mode, hipOwnerType, registrationStatus, abdmHipId, supports:
 *   { careContext, healthInformationTransfer, prescriptions, labReports, delegatedMode } }} */
export async function getCapabilities() {
  try {
    const { data } = await apiClient.get(`${BASE}/capabilities`);
    const s = data.supports || {};
    return {
      mode: data.mode,
      hipOwnerType: data.hip_owner_type,
      registrationStatus: data.registration_status,
      abdmHipId: data.abdm_hip_id,
      supports: {
        careContext: !!s.care_context,
        healthInformationTransfer: !!s.health_information_transfer,
        prescriptions: !!s.prescriptions,
        labReports: !!s.lab_reports,
        delegatedMode: !!s.delegated_mode,
      },
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/** @returns {{ abdmMode, hipOwnerType, registrationStatus, abdmHipId, abdmRegisteredName,
 *   abdmFacilityId, abdmPublishingEnabled }} */
export async function getRegistrationStatus() {
  try {
    const { data } = await apiClient.get(`${BASE}/registration/status`);
    return {
      abdmMode: data.abdm_mode,
      hipOwnerType: data.hip_owner_type,
      registrationStatus: data.registration_status,
      abdmHipId: data.abdm_hip_id,
      abdmRegisteredName: data.abdm_registered_name,
      abdmNameSource: data.abdm_name_source,
      abdmHipStatus: data.abdm_hip_status,
      abdmHipStatusReason: data.abdm_hip_status_reason,
      abdmFacilityId: data.abdm_facility_id,
      abdmPublishingEnabled: !!data.abdm_publishing_enabled,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

// ── Visits — the primary M2 listing ─────────────────────────────────────

/**
 * Every COMPLETED visit in a date range, descending by visit date, for ABHA-VERIFIED
 * patients only (unverified/no-ABHA patients' visits never appear here — that's a
 * deliberate filter, not a gap; those patients get the manual "Send link SMS" button).
 * Server-side paginated (page/pageSize drive real pagination, not a client-side slice)
 * — see app/api/hip.py:list_hip_visits for why.
 *
 * @param {{ dateFrom: string, dateTo: string, page?: number, pageSize?: number }} params
 * @returns {{ visits: Array<{ visitId, patientId, patientName, visitDate, careContextId,
 *   careContextStatus }>, totalCount, page, pageSize }}
 */
export async function getVisits({ dateFrom, dateTo, page = 1, pageSize = 20, q }) {
  try {
    const { data } = await apiClient.get(`${BASE}/visits`, {
      params: { date_from: dateFrom, date_to: dateTo, page, page_size: pageSize, ...(q ? { q } : {}) },
    });
    return {
      visits: (data.visits || []).map((v) => ({
        visitId: v.visit_id,
        patientId: v.patient_id,
        patientName: v.patient_name,
        visitDate: v.visit_date,
        uhid: v.uhid,
        visitTime: v.visit_time,
        tokenNumber: v.token_number,
        appointmentNumber: v.appointment_number,
        doctorName: v.doctor_name,
        departmentName: v.department_name,
        hasPrescription: v.has_prescription,
        reportCount: v.report_count,
        patientMobileMasked: v.patient_mobile_masked,
        careContextId: v.care_context_id,
        careContextStatus: v.care_context_status,
        lastLinkAttemptAt: v.last_link_attempt_at,
        linkError: v.link_error,
        retryAfter: v.retry_after,
      })),
      totalCount: data.total_count,
      page: data.page,
      pageSize: data.page_size,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

// ── IPD discharges — discharge-summary publish ──────────────────────────

/**
 * DISCHARGED, ABHA-verified-by-discharge admissions in a date range (max 31 days),
 * newest discharge first, server-side paginated (page_size 1..50).
 * @param {{ dateFrom: string, dateTo: string, page?: number, pageSize?: number }} params
 * @returns {{ discharges: Array<{ admissionId, patientId, patientName, ipNumber, admittedAt,
 *   dischargedAt, summaryWritten, careContextId, careContextStatus }>, totalCount, page, pageSize }}
 */
export async function listDischarges({ dateFrom, dateTo, page = 1, pageSize = 20, q }) {
  try {
    const { data } = await apiClient.get(`${BASE}/discharges`, {
      params: { date_from: dateFrom, date_to: dateTo, page, page_size: pageSize, ...(q ? { q } : {}) },
    });
    return {
      discharges: (data.discharges || []).map((d) => ({
        admissionId: d.admission_id,
        patientId: d.patient_id,
        patientName: d.patient_name,
        ipNumber: d.ip_number,
        uhid: d.uhid,
        doctorName: d.doctor_name,
        departmentName: d.department_name,
        hasDischargeSummary: d.has_discharge_summary,
        admittedAt: d.admitted_at,
        dischargedAt: d.discharged_at,
        summaryWritten: !!d.summary_written,
        careContextId: d.care_context_id ?? null,
        careContextStatus: d.care_context_status ?? null,
        lastLinkAttemptAt: d.last_link_attempt_at,
        linkError: d.link_error,
        retryAfter: d.retry_after,
      })),
      totalCount: data.total_count,
      page: data.page,
      pageSize: data.page_size,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Publish an admission's discharge summary to ABDM. ADMIN/DOCTOR only. Idempotent — safe to
 * repeat. 422 errors carry err.code (NOT_DISCHARGED | ABHA_NOT_VERIFIED |
 * RECORD_BEFORE_ABHA_LINK | DISCHARGE_SUMMARY_NOT_WRITTEN | INCOMPLETE_RECORD) and, for
 * INCOMPLETE_RECORD, err.missing[]; 500 BUNDLE_NOT_SAVED; 503 IPD_TABLE_UNAVAILABLE.
 * @returns {{ careContextId, status, bundlePublished }}
 */
export async function publishDischargeSummary(admissionId) {
  try {
    const { data } = await apiClient.post(`${BASE}/discharges/${encodeURIComponent(admissionId)}/publish`);
    return {
      careContextId: data.care_context_id,
      status: data.status,
      bundlePublished: !!data.bundle_published,
    };
  } catch (error) {
    const err = _buildError(error);
    const missing = error?.response?.data?.detail?.missing;
    if (Array.isArray(missing)) err.missing = missing;
    throw err;
  }
}

// ── Care context — publish / retry ──────────────────────────────────────

/**
 * Publish a visit to ABDM as a care context. Idempotent — a repeat call for the same
 * (patientId, visitId, recordType) safely resolves to the same care context rather than
 * creating a duplicate, so this is also the correct "retry" action for a
 * bundlePublished:false partial-success result — just call it again.
 *
 * @param {{ patientId: string, visitId: string, recordType?: string }} params
 * @returns {{ careContextId, status, bundlePublished }}
 */
export async function createCareContext({ patientId, visitId, recordType = 'OPConsultation' }) {
  try {
    const { data } = await apiClient.post(`${BASE}/care-context/add`, {
      patient_id: patientId,
      visit_id: visitId,
      record_type: recordType,
    });
    return {
      careContextId: data.care_context_id,
      status: data.status,
      bundlePublished: !!data.bundle_published,
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/** Resend/retry a stuck link notification — valid only for CREATED/FAILED care contexts.
 * @returns {{ careContextId, status }} */
export async function resendLinkNotify({ careContextId }) {
  try {
    const { data } = await apiClient.post(`${BASE}/care-context/link/notify`, {
      care_context_id: careContextId,
    });
    return { careContextId: data.care_context_id, status: data.status };
  } catch (err) {
    throw _buildError(err);
  }
}

/** Re-run the ABDM link for one stuck care context. 409 ALREADY_LINKED / NOT_RETRYABLE / LINK_IN_PROGRESS and
 * 429 LINK_BLOCKED (err.retryAfter = seconds) come back as err.code, server text in err.userMessage.
 * @returns {{ status }} (200 body also has care_context_id, link:"started") */
export async function retryLink({ careContextId }) {
  try {
    const { data } = await apiClient.post(`${BASE}/care-context/${encodeURIComponent(careContextId)}/retry-link`);
    return { status: data.status };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Care contexts for one patient, newest first, plus the patient's ABHA identity (contract 2.12).
 * visit_at may be date-only; linkedAt is null for older rows.
 * @returns {{ abhaNumber: string|null, abhaAddress: string|null, careContexts: Array<{
 *   careContextId, reference, display, hiType, visitAt, linkStatus, linkedAt }> }}
 */
export async function listCareContexts(patientId) {
  try {
    const { data } = await apiClient.get(`${BASE}/care-context/list`, {
      params: { patient_id: patientId },
    });
    return {
      abhaNumber: data.abha_number ?? null,
      abhaAddress: data.abha_address ?? null,
      careContexts: (data.care_contexts || []).map((c) => ({
        careContextId: c.care_context_id,
        reference: c.reference,
        display: c.display,
        hiType: c.hi_type,
        visitAt: c.visit_at ?? null,
        linkStatus: c.link_status,
        linkedAt: c.linked_at ?? null,
      })),
    };
  } catch (err) {
    throw _buildError(err);
  }
}

/**
 * Send the ABDM-app deep-link SMS to a mobile number (manual button; the auto-send on
 * visit completion was removed). Backend: ADMIN/DOCTOR/RECEPTIONIST per owner R30.
 * @param {string} phoneNo 10-digit mobile
 * @returns {{ status: string }}
 */
export async function sendDeepLinkSms(phoneNo) {
  try {
    const { data } = await apiClient.post(`${BASE}/deep-link/notify-sms`, { phone_no: phoneNo });
    return { status: data.status };
  } catch (err) {
    throw _buildError(err);
  }
}
